/**
 * The manual payroll inputs a person types or pastes — roadmap 2026-09-10 A-6 / B3, the
 * first half of E5-4. Pure: no DOM, no request. `web/shapes/payroll.md` §7a is the shape.
 *
 * `POST /payroll/runs/{id}/calculate` takes `employee_inputs[]` (`employeeInputSchema`,
 * `src/modules/payroll/service.ts`). The web's Calculate button used to send `{}`, so
 * Lương CB, tỷ lệ đạt, phụ cấp, thưởng, tạm ứng and the dependant count were zero on
 * every payslip a browser calculated. This module turns a grid of typed text into exactly
 * the body a person meant, and nothing else:
 *
 *  - **A field nobody typed is absent from the wire.** The server defaults it to 0; a
 *    client that sent `0` would be claiming a figure nobody entered. A person with nothing
 *    typed is absent from `employee_inputs`, and a grid with nothing typed sends `{}` —
 *    not `{ employee_inputs: [] }`.
 *  - **The bounds are the server's**, transcribed once in `PAYROLL_INPUT_BOUNDS` and
 *    pinned against `employeeInputSchema` by `employee-inputs.test.ts`, which reads the
 *    file. None is invented here: a field the schema leaves unbounded above stays so.
 *  - **No number is guessed.** An amount is whole đồng in digits only — `500000`
 *    (`PAYROLL_INPUT_FORMAT`, fix round 1, R19). In a Vietnamese spreadsheet `.` groups
 *    thousands and `,` marks decimals, so `500.000` is five hundred thousand; a reader
 *    that took the `.` for a decimal point would send 500 — a payslip a thousand times
 *    too low, or a `tam_ung` that overpays net — with nothing on screen to say so. Any
 *    `.` or `,` in an amount is therefore refused with a sentence that names the
 *    thousands-separator mistake. The rate keeps an optional `.` decimal part (`0.9`);
 *    `0,9` and every other separator are refused there too.
 *  - **`phat` is offered again, lawfully** (attendance-core Task 5, reversing roadmap
 *    §2's removal). An amount travels only with a ground CODE and a written reason —
 *    never alone. The ground is one of the company's OWN configured
 *    `deduction_grounds` (`PayrollFormulaParams`, `src/modules/payroll/engine.ts`):
 *    there is no fixed, universal list here or anywhere in this codebase, because
 *    which grounds a deduction may cite is one tenant's own rule, not a fact this
 *    screen knows in advance. This module therefore cannot validate a typed ground
 *    against the tenant's own list (it would need the active formula version's own
 *    `deduction_grounds`, which this panel does not fetch); it can only check that a
 *    ground and a reason of a plausible length travel together with the amount.
 *    `readPenaltyCells` is that shape check — the cross-field half of
 *    `employeeInputSchema`'s own `.superRefine` — so the grid can say why before the
 *    press for the checks it CAN make, and the server still refuses, by field, a
 *    ground naming nothing the active formula version configured.
 *
 * CSV is parsed here by hand — no dependency. The delimiter is a comma, a semicolon or a
 * TAB (a paste from Excel is TAB-separated), detected from the header row. RFC 4180
 * double quotes are honoured; a BOM and CRLF line ends are tolerated; blank lines are
 * skipped. The header names are the wire keys: `employee_id` plus any of
 * `PAYROLL_INPUT_FIELDS`, in any case and any order. A column with **no** name is the
 * extra column somebody selected in Excel: blank on every row it is ignored, and holding
 * data it refuses the paste by position rather than as `""` (final review M3).
 */

import type { PayrollEmployeeInput } from '../api/payroll';
import type { MessageKey } from '../i18n/messages';

/** The manual fields this screen offers, in the order the grid shows them. */
export const PAYROLL_INPUT_FIELDS = [
  'luong_cb',
  'luong_nang_suat_hd',
  'ty_le_dat',
  'phu_cap_dong_phuc',
  'phu_cap_xang',
  'phu_cap_khac',
  'thuong',
  'phat',
  'tam_ung',
  'so_nguoi_phu_thuoc',
] as const;

export type PayrollInputField = (typeof PAYROLL_INPUT_FIELDS)[number];

export interface InputBounds {
  readonly min: number;
  /** `null`: the schema sets no maximum, and neither does this screen. */
  readonly max: number | null;
  readonly integer: boolean;
}

/** `employeeInputSchema`'s bounds, field by field. Pinned by `employee-inputs.test.ts`. */
export const PAYROLL_INPUT_BOUNDS: Readonly<Record<PayrollInputField, InputBounds>> = {
  luong_cb: { min: 0, max: null, integer: true },
  luong_nang_suat_hd: { min: 0, max: null, integer: true },
  ty_le_dat: { min: 0, max: 3, integer: false },
  phu_cap_dong_phuc: { min: 0, max: null, integer: true },
  phu_cap_xang: { min: 0, max: null, integer: true },
  phu_cap_khac: { min: 0, max: null, integer: true },
  thuong: { min: 0, max: null, integer: true },
  phat: { min: 0, max: null, integer: true },
  tam_ung: { min: 0, max: null, integer: true },
  so_nguoi_phu_thuoc: { min: 0, max: 20, integer: true },
};

/**
 * How a field is written — this screen's reading rule, which is not a bound (R19).
 *
 *  - `dong`: an amount of money, in whole đồng, digits only — `500000`. A `.` or `,` is
 *    refused, never read: on Vietnamese settings `500.000` is five hundred thousand, and
 *    the one fraction a person could mean here is that misreading. `employeeInputSchema`
 *    would take a fraction; no payslip amount has one — the engine rounds every component
 *    to whole đồng.
 *  - `decimal`: digits with an optional `.` decimal part — tỷ lệ đạt (`0.9` = 90%), and
 *    the dependant count, which the schema's `.int()` then holds to a whole number.
 *
 * The eight `dong` fields are exactly those whose hint says đồng; a test pins the two
 * maps together.
 */
export type InputFormat = 'dong' | 'decimal';

export const PAYROLL_INPUT_FORMAT: Readonly<Record<PayrollInputField, InputFormat>> = {
  luong_cb: 'dong',
  luong_nang_suat_hd: 'dong',
  ty_le_dat: 'decimal',
  phu_cap_dong_phuc: 'dong',
  phu_cap_xang: 'dong',
  phu_cap_khac: 'dong',
  thuong: 'dong',
  phat: 'dong',
  tam_ung: 'dong',
  so_nguoi_phu_thuoc: 'decimal',
};

/** The two text cells that make a `phat` lawful. Not numbers, so not in `PAYROLL_INPUT_FIELDS`. */
export const PAYROLL_PENALTY_FIELDS = ['phat_can_cu', 'phat_ly_do'] as const;
export type PayrollPenaltyField = (typeof PAYROLL_PENALTY_FIELDS)[number];

export type PenaltyProblem =
  'basis_required' | 'reason_required' | 'reason_too_short' | 'amount_required';

export const PENALTY_PROBLEM_LABEL: Readonly<Record<PenaltyProblem, MessageKey>> = {
  basis_required: 'payroll.inputs.penalty.problem.basis_required',
  reason_required: 'payroll.inputs.penalty.problem.reason_required',
  reason_too_short: 'payroll.inputs.penalty.problem.reason_too_short',
  amount_required: 'payroll.inputs.penalty.problem.amount_required',
};

export const PENALTY_REASON_MIN = 10;
export const PENALTY_REASON_MAX = 500;

/**
 * The problems one person's three penalty cells hold, together. Never one in isolation:
 * an amount with no ground is `basis_required` even though the ground cell itself is
 * blank rather than wrong, because the DEFECT is in the combination, not in one cell.
 * Mirrors the SHAPE half of `employeeInputSchema`'s `.superRefine` (`service.ts`) — that
 * a ground and a reason of a plausible length travel with the amount. It does NOT, and
 * cannot, check that the typed ground is one the company has actually configured: that
 * list is the active formula version's own `deduction_grounds`, which this module does
 * not fetch, so an unrecognised ground is a `400` this cell only learns about after the
 * press, named by field, not a problem shown here.
 */
export function readPenaltyCells(
  amount: string,
  basis: string,
  reason: string,
): readonly PenaltyProblem[] {
  const problems: PenaltyProblem[] = [];
  const { value } = readInputCell('phat', amount);
  const hasAmount = value !== null && value > 0;
  const trimmedBasis = basis.trim();
  const trimmedReason = reason.trim();
  const basisEntered = trimmedBasis !== '';
  const reasonEntered = trimmedReason !== '';
  if (hasAmount) {
    if (!basisEntered) problems.push('basis_required');
    if (!reasonEntered) problems.push('reason_required');
    else if (trimmedReason.length < PENALTY_REASON_MIN) problems.push('reason_too_short');
  } else if (basisEntered || reasonEntered) {
    problems.push('amount_required');
  }
  return problems;
}

/** `calculateRunSchema`'s `employee_inputs` `.max(2000)`. */
export const MAX_EMPLOYEE_INPUTS = 2000;

/** Every column a CSV may name, in the order the hint lists them. */
export const ACCEPTED_CSV_COLUMNS: readonly string[] = [
  'employee_id',
  ...PAYROLL_INPUT_FIELDS,
  ...PAYROLL_PENALTY_FIELDS,
];

/** Each field's name on screen. Eight are payslip components already; the count is not. */
export const PAYROLL_INPUT_LABEL: Readonly<Record<PayrollInputField, MessageKey>> = {
  luong_cb: 'payroll.component.luong_cb',
  luong_nang_suat_hd: 'payroll.component.luong_nang_suat_hd',
  ty_le_dat: 'payroll.component.ty_le_dat',
  phu_cap_dong_phuc: 'payroll.component.phu_cap_dong_phuc',
  phu_cap_xang: 'payroll.component.phu_cap_xang',
  phu_cap_khac: 'payroll.component.phu_cap_khac',
  thuong: 'payroll.component.thuong',
  phat: 'payroll.component.phat',
  tam_ung: 'payroll.component.tam_ung',
  so_nguoi_phu_thuoc: 'payroll.inputs.field.so_nguoi_phu_thuoc',
};

/** How each field is written, under its name. */
export const PAYROLL_INPUT_HINT: Readonly<Record<PayrollInputField, MessageKey>> = {
  luong_cb: 'payroll.inputs.hint.money',
  luong_nang_suat_hd: 'payroll.inputs.hint.money',
  ty_le_dat: 'payroll.inputs.hint.ty_le_dat',
  phu_cap_dong_phuc: 'payroll.inputs.hint.money',
  phu_cap_xang: 'payroll.inputs.hint.money',
  phu_cap_khac: 'payroll.inputs.hint.money',
  thuong: 'payroll.inputs.hint.money',
  phat: 'payroll.inputs.hint.money',
  tam_ung: 'payroll.inputs.hint.money',
  so_nguoi_phu_thuoc: 'payroll.inputs.hint.dependants',
};

export type CellProblem =
  'not_a_number' | 'not_whole_dong' | 'below_min' | 'above_max' | 'not_an_integer';

export const CELL_PROBLEM_LABEL: Readonly<Record<CellProblem, MessageKey>> = {
  not_a_number: 'payroll.inputs.problem.not_a_number',
  not_whole_dong: 'payroll.inputs.problem.not_whole_dong',
  below_min: 'payroll.inputs.problem.below_min',
  above_max: 'payroll.inputs.problem.above_max',
  not_an_integer: 'payroll.inputs.problem.not_an_integer',
};

export interface CellReading {
  /** Something other than whitespace was typed. */
  readonly entered: boolean;
  /** The number to send; `null` when nothing was typed or what was typed is refused. */
  readonly value: number | null;
  readonly problem: CellProblem | null;
}

/** `idString` of `service.ts`. */
const EMPLOYEE_ID = /^[1-9][0-9]{0,18}$/;
const DECIMAL = /^\d+(?:\.\d+)?$/;
const INTEGER = /^\d+$/;
const NEGATIVE = /^-\d+(?:\.\d+)?$/;

export function readInputCell(field: PayrollInputField, raw: string): CellReading {
  const text = raw.trim();
  if (text === '') return { entered: false, value: null, problem: null };
  const bounds = PAYROLL_INPUT_BOUNDS[field];
  if (NEGATIVE.test(text)) return { entered: true, value: null, problem: 'below_min' };
  if (PAYROLL_INPUT_FORMAT[field] === 'dong') {
    // Whole đồng, digits only: `500.000` is refused here, never read as 500 (R19).
    if (!INTEGER.test(text)) return { entered: true, value: null, problem: 'not_whole_dong' };
  } else if (!DECIMAL.test(text)) {
    return { entered: true, value: null, problem: 'not_a_number' };
  }
  if (bounds.integer && !INTEGER.test(text)) {
    return { entered: true, value: null, problem: 'not_an_integer' };
  }
  const value = Number(text);
  // Digits alone can still overflow to Infinity, which JSON cannot carry.
  if (!Number.isFinite(value)) return { entered: true, value: null, problem: 'not_a_number' };
  if (value < bounds.min) return { entered: true, value: null, problem: 'below_min' };
  if (bounds.max !== null && value > bounds.max) {
    return { entered: true, value: null, problem: 'above_max' };
  }
  return { entered: true, value, problem: null };
}

/** Typed text by employee id, by field. A key that is absent was never typed. */
export type InputGrid = Readonly<
  Record<string, Readonly<Partial<Record<PayrollInputField | PayrollPenaltyField, string>>>>
>;

export function setGridCell(
  grid: InputGrid,
  employeeId: string,
  field: PayrollInputField | PayrollPenaltyField,
  raw: string,
): InputGrid {
  return { ...grid, [employeeId]: { ...grid[employeeId], [field]: raw } };
}

export interface GridProblem {
  readonly employeeId: string;
  readonly field: PayrollInputField | PayrollPenaltyField;
  readonly problem: CellProblem | PenaltyProblem;
}

/** Which cell a penalty problem is said beside. */
const PENALTY_PROBLEM_FIELD: Readonly<
  Record<PenaltyProblem, PayrollInputField | PayrollPenaltyField>
> = {
  basis_required: 'phat_can_cu',
  reason_required: 'phat_ly_do',
  reason_too_short: 'phat_ly_do',
  amount_required: 'phat',
};

/** Every cell of a roster employee that holds something the server would refuse. */
export function gridProblems(
  grid: InputGrid,
  rosterIds: readonly string[],
): readonly GridProblem[] {
  const problems: GridProblem[] = [];
  for (const employeeId of rosterIds) {
    for (const field of PAYROLL_INPUT_FIELDS) {
      const { problem } = readInputCell(field, grid[employeeId]?.[field] ?? '');
      if (problem !== null) problems.push({ employeeId, field, problem });
    }
    const cells = grid[employeeId] ?? {};
    for (const problem of readPenaltyCells(
      cells.phat ?? '',
      cells.phat_can_cu ?? '',
      cells.phat_ly_do ?? '',
    )) {
      problems.push({ employeeId, field: PENALTY_PROBLEM_FIELD[problem], problem });
    }
  }
  return problems;
}

export interface GridSummary {
  /** Cells holding a number that will be sent. */
  readonly enteredCells: number;
  /** People with at least one such cell — the length of `employee_inputs`. */
  readonly employeesWithInputs: number;
}

export function gridSummary(grid: InputGrid, rosterIds: readonly string[]): GridSummary {
  let enteredCells = 0;
  let employeesWithInputs = 0;
  for (const employeeId of rosterIds) {
    let cells = 0;
    for (const field of PAYROLL_INPUT_FIELDS) {
      if (readInputCell(field, grid[employeeId]?.[field] ?? '').value !== null) cells += 1;
    }
    enteredCells += cells;
    if (cells > 0) employeesWithInputs += 1;
  }
  return { enteredCells, employeesWithInputs };
}

/**
 * The `employee_inputs` a grid means: one entry per roster employee with at least one
 * typed, valid cell, carrying those cells and no others, in roster order. Never sent
 * while `gridProblems` is non-empty — the Calculate control is absent then.
 *
 * `phat` travels only together with a valid `phat_can_cu` and a `phat_ly_do` of at
 * least `PENALTY_REASON_MIN` characters — never alone. Sending an amount with no basis
 * is a guaranteed `400` (`employeeInputSchema`'s `.superRefine`, `service.ts`), and a
 * cell this module cannot yet vouch for is a cell nothing is sent for, the same rule
 * every other field here already follows for a value it cannot parse.
 */
export function employeeInputsOf(
  grid: InputGrid,
  rosterIds: readonly string[],
): readonly PayrollEmployeeInput[] {
  const inputs: PayrollEmployeeInput[] = [];
  for (const employeeId of rosterIds) {
    const cells = grid[employeeId] ?? {};
    const entered: Partial<Record<PayrollInputField, number>> & {
      phat_can_cu?: string;
      phat_ly_do?: string;
    } = {};
    for (const field of PAYROLL_INPUT_FIELDS) {
      if (field === 'phat') continue; // handled below, together with its basis and reason
      const { value } = readInputCell(field, cells[field] ?? '');
      if (value !== null) entered[field] = value;
    }
    const { value: phatValue } = readInputCell('phat', cells.phat ?? '');
    const basis = (cells.phat_can_cu ?? '').trim();
    const reason = (cells.phat_ly_do ?? '').trim();
    // Whether `basis` is one of the company's OWN configured grounds is for the server
    // to say (this module has no fetched list to check it against) — only that the
    // three plausible-looking cells travel together, never `phat` alone.
    if (phatValue !== null && basis !== '' && reason.length >= PENALTY_REASON_MIN) {
      entered.phat = phatValue;
      entered.phat_can_cu = basis;
      entered.phat_ly_do = reason;
    }
    if (Object.keys(entered).length > 0) inputs.push({ employee_id: employeeId, ...entered });
  }
  return inputs;
}

/* ─────────────────────────────────────────────────────────────────────── CSV ── */

export type CsvIssueKind =
  | 'empty'
  | 'unclosed_quote'
  | 'no_employee_id_column'
  | 'unknown_column'
  | 'nameless_column'
  | 'duplicate_column'
  | 'too_many_rows'
  | 'cell_count'
  | 'bad_employee_id'
  | 'not_in_roster'
  | 'duplicate_employee';

export const CSV_ISSUE_LABEL: Readonly<Record<CsvIssueKind, MessageKey>> = {
  empty: 'payroll.inputs.csv.issue.empty',
  unclosed_quote: 'payroll.inputs.csv.issue.unclosed_quote',
  no_employee_id_column: 'payroll.inputs.csv.issue.no_employee_id_column',
  unknown_column: 'payroll.inputs.csv.issue.unknown_column',
  nameless_column: 'payroll.inputs.csv.issue.nameless_column',
  duplicate_column: 'payroll.inputs.csv.issue.duplicate_column',
  too_many_rows: 'payroll.inputs.csv.issue.too_many_rows',
  cell_count: 'payroll.inputs.csv.issue.cell_count',
  bad_employee_id: 'payroll.inputs.csv.issue.bad_employee_id',
  not_in_roster: 'payroll.inputs.csv.issue.not_in_roster',
  duplicate_employee: 'payroll.inputs.csv.issue.duplicate_employee',
};

export interface CsvIssue {
  /** The 1-based line of the pasted text; `0` for the text as a whole. */
  readonly line: number;
  readonly kind: CsvIssueKind;
  /**
   * The column, employee id or counts the sentence names; `''` when it names none. For
   * `nameless_column` it is the column's 1-based position, because that column has no
   * name to quote and `""` reads as a column called nothing (final review M3).
   */
  readonly detail: string;
}

export interface CsvApplied {
  /** Every accepted row written in — or the grid passed in, itself, when the header was refused. */
  readonly grid: InputGrid;
  readonly appliedRows: number;
  readonly issues: readonly CsvIssue[];
}

interface CsvRow {
  readonly line: number;
  readonly cells: readonly string[];
}

function isInputField(column: string): column is PayrollInputField {
  return (PAYROLL_INPUT_FIELDS as readonly string[]).includes(column);
}

function isPenaltyField(column: string): column is PayrollPenaltyField {
  return (PAYROLL_PENALTY_FIELDS as readonly string[]).includes(column);
}

/** The delimiter the header row uses: a TAB (an Excel paste), else `;`, else `,`. */
function delimiterOf(headerLine: string): string {
  if (headerLine.includes('\t')) return '\t';
  if (headerLine.includes(';') && !headerLine.includes(',')) return ';';
  return ',';
}

/**
 * RFC 4180 by hand, in one pass. A cell that starts with `"` is quoted: it may hold the
 * delimiter and line breaks, and `""` inside it is one `"`. `unclosedAt` is the line a
 * quote opened on and never closed, or `null`.
 */
function splitCsv(
  text: string,
  delimiter: string,
): { readonly rows: readonly CsvRow[]; readonly unclosedAt: number | null } {
  const rows: CsvRow[] = [];
  let cells: string[] = [];
  let cell = '';
  let quoted = false;
  let line = 1;
  let rowLine = 1;
  let quoteLine = 0;
  for (let index = 0; index < text.length; index += 1) {
    const char = text.charAt(index);
    if (quoted) {
      if (char === '"' && text.charAt(index + 1) === '"') {
        cell += '"';
        index += 1;
      } else if (char === '"') {
        quoted = false;
      } else {
        if (char === '\n') line += 1;
        cell += char;
      }
    } else if (char === '"' && cell === '') {
      quoted = true;
      quoteLine = line;
    } else if (char === delimiter) {
      cells.push(cell);
      cell = '';
    } else if (char === '\n') {
      cells.push(cell);
      rows.push({ line: rowLine, cells });
      cells = [];
      cell = '';
      line += 1;
      rowLine = line;
    } else {
      cell += char;
    }
  }
  if (quoted) return { rows, unclosedAt: quoteLine };
  cells.push(cell);
  rows.push({ line: rowLine, cells });
  return { rows, unclosedAt: null };
}

/**
 * Write a pasted CSV into a grid. A problem with the header — or more people than one
 * calculation takes — applies nothing. A problem with one row skips that row and says
 * which line. Values are written as text, so the grid's own validation speaks for them.
 */
export function applyInputCsv(
  grid: InputGrid,
  text: string,
  rosterIds: readonly string[],
): CsvApplied {
  const refuse = (issues: readonly CsvIssue[]): CsvApplied => ({ grid, appliedRows: 0, issues });
  const normalized = text.replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n');
  const headerLine = normalized.split('\n').find((candidate) => candidate.trim() !== '');
  if (headerLine === undefined) return refuse([{ line: 0, kind: 'empty', detail: '' }]);

  const { rows, unclosedAt } = splitCsv(normalized, delimiterOf(headerLine));
  if (unclosedAt !== null) {
    return refuse([{ line: unclosedAt, kind: 'unclosed_quote', detail: '' }]);
  }
  const [header, ...body] = rows.filter((row) => row.cells.some((cell) => cell.trim() !== ''));
  if (header === undefined) return refuse([{ line: 0, kind: 'empty', detail: '' }]);

  const columns = header.cells.map((cell) => cell.trim().toLowerCase());
  const headerIssues: CsvIssue[] = [];
  const seen = new Set<string>();
  for (const [index, column] of columns.entries()) {
    /*
      A column with no name at all — an extra column inside the range somebody selected
      in Excel, which copies out as a trailing delimiter on every line. Blank on every
      row it says nothing, so it is ignored rather than refusing the paste as a column
      called `""`; two of them used to add a duplicate-column message on top of that
      (final review M3). Carrying data it IS data nobody named, so the paste is refused —
      by position, since there is no name to quote.
    */
    if (column === '') {
      if (body.some((row) => (row.cells[index] ?? '').trim() !== '')) {
        headerIssues.push({
          line: header.line,
          kind: 'nameless_column',
          detail: String(index + 1),
        });
      }
      continue;
    }
    if (seen.has(column)) {
      headerIssues.push({ line: header.line, kind: 'duplicate_column', detail: column });
    }
    seen.add(column);
    if (column === 'employee_id' || isInputField(column) || isPenaltyField(column)) continue;
    headerIssues.push({ line: header.line, kind: 'unknown_column', detail: column });
  }
  if (!seen.has('employee_id')) {
    headerIssues.push({ line: header.line, kind: 'no_employee_id_column', detail: '' });
  }
  if (body.length > MAX_EMPLOYEE_INPUTS) {
    headerIssues.push({ line: 0, kind: 'too_many_rows', detail: String(body.length) });
  }
  if (headerIssues.length > 0) return refuse(headerIssues);

  const idIndex = columns.indexOf('employee_id');
  const roster = new Set(rosterIds);
  const issues: CsvIssue[] = [];
  const rowsByEmployee = new Map<string, CsvRow[]>();
  for (const row of body) {
    if (row.cells.length !== columns.length) {
      issues.push({
        line: row.line,
        kind: 'cell_count',
        detail: `${String(row.cells.length)}/${String(columns.length)}`,
      });
      continue;
    }
    const employeeId = (row.cells[idIndex] ?? '').trim();
    if (!EMPLOYEE_ID.test(employeeId)) {
      issues.push({ line: row.line, kind: 'bad_employee_id', detail: employeeId });
      continue;
    }
    if (!roster.has(employeeId)) {
      issues.push({ line: row.line, kind: 'not_in_roster', detail: employeeId });
      continue;
    }
    rowsByEmployee.set(employeeId, [...(rowsByEmployee.get(employeeId) ?? []), row]);
  }

  let next = grid;
  let appliedRows = 0;
  for (const [employeeId, rowsOfOne] of rowsByEmployee) {
    const [only, ...more] = rowsOfOne;
    if (only === undefined) continue;
    if (more.length > 0) {
      // Two rows for one person: which one is right is not this parser's to guess.
      for (const row of rowsOfOne) {
        issues.push({ line: row.line, kind: 'duplicate_employee', detail: employeeId });
      }
      continue;
    }
    for (const [index, column] of columns.entries()) {
      if (isInputField(column) || isPenaltyField(column)) {
        next = setGridCell(next, employeeId, column, (only.cells[index] ?? '').trim());
      }
    }
    appliedRows += 1;
  }
  return {
    grid: next,
    appliedRows,
    issues: [...issues].sort((left, right) => left.line - right.line),
  };
}
